import "./utility-tools.css";
import { createOperationResult } from "./operation-result.js";
import { formatFileSize, safeBaseName } from "./utility-tools-core.js";
import {
  hasWebPHeader,
  gifRepeatCount,
  normalizeGifDelay,
  parseWebP,
  validateWebPForGif,
  webPDescription,
  webPFrameBytes
} from "./webp-to-gif-core.js";

const root = document.querySelector("[data-webp-to-gif]");

if (root) {
  const ids = [
    "webpGifStatus", "webpGifUploadPanel", "webpGifDropZone", "chooseWebp",
    "webpFileInput", "webpGifWorkspace", "webpPreview", "webpFileName",
    "webpDetails", "gifColors", "convertWebp", "replaceWebp", "webpProgress"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    file: null,
    bytes: null,
    metadata: null,
    previewUrl: null,
    worker: null,
    pending: new Map(),
    requestId: 0,
    jobId: 0,
    busy: false
  };
  const result = createOperationResult(root, elements.webpGifWorkspace, {
    onReset: () => cleanup()
  });

  function showStatus(message = "", kind = "error") {
    elements.webpGifStatus.textContent = message;
    elements.webpGifStatus.dataset.kind = kind;
    elements.webpGifStatus.hidden = !message;
  }

  function setBusy(busy) {
    state.busy = busy;
    root.setAttribute("aria-busy", String(busy));
    elements.convertWebp.disabled = busy || !state.metadata;
    elements.replaceWebp.disabled = busy;
    elements.gifColors.disabled = busy;
    elements.chooseWebp.disabled = busy;
    elements.webpProgress.hidden = !busy;
  }

  function cleanup() {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    state.previewUrl = null;
    state.worker?.terminate();
    state.worker = null;
    state.pending.clear();
  }

  function worker() {
    if (state.worker) return state.worker;
    const instance = new Worker("/dist/webp-to-gif-worker.js", { type: "module" });
    instance.addEventListener("message", event => {
      const pending = state.pending.get(event.data.requestId);
      if (!pending) return;
      state.pending.delete(event.data.requestId);
      if (event.data.ok) pending.resolve(event.data);
      else pending.reject(new Error(event.data.error));
    });
    instance.addEventListener("error", event => {
      for (const pending of state.pending.values()) pending.reject(new Error(event.message || "The GIF encoder stopped unexpectedly."));
      state.pending.clear();
      instance.terminate();
      state.worker = null;
    });
    state.worker = instance;
    return instance;
  }

  function sendWorker(action, payload = {}, transfer = []) {
    const requestId = ++state.requestId;
    return new Promise((resolve, reject) => {
      state.pending.set(requestId, { resolve, reject });
      worker().postMessage({ requestId, action, ...payload }, transfer);
    });
  }

  async function decodedImage(blob) {
    if (typeof createImageBitmap === "function") return createImageBitmap(blob);
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      image.decoding = "async";
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function closeImage(image) {
    if (typeof image?.close === "function") image.close();
  }

  function paintBackground(context, metadata, frame = null) {
    const { background } = metadata;
    const x = frame?.x ?? 0;
    const y = frame?.y ?? 0;
    const width = frame?.width ?? metadata.width;
    const height = frame?.height ?? metadata.height;
    context.clearRect(x, y, width, height);
    if (background[3] > 0) {
      context.fillStyle = `rgba(${background[0]}, ${background[1]}, ${background[2]}, ${background[3] / 255})`;
      context.fillRect(x, y, width, height);
    }
  }

  async function load(file) {
    if (!file || state.busy) return;
    showStatus();
    setBusy(true);
    try {
      if (file.size > 25 * 1024 * 1024) throw new Error("WebP files are limited to 25 MB.");
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!hasWebPHeader(bytes)) throw new Error("File content is not a valid WebP image.");
      const metadata = validateWebPForGif(parseWebP(bytes), file.size);
      const decoded = await decodedImage(file);
      closeImage(decoded);

      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(file);
      elements.webpPreview.src = state.previewUrl;
      elements.webpFileName.textContent = file.name;
      elements.webpDetails.textContent = `${webPDescription(metadata)} · ${formatFileSize(file.size)}`;
      state.file = file;
      state.bytes = bytes;
      state.metadata = metadata;
      elements.webpGifUploadPanel.hidden = true;
      elements.webpGifWorkspace.hidden = false;
    } catch (error) {
      state.file = null;
      state.bytes = null;
      state.metadata = null;
      showStatus(error?.message || "This WebP image could not be opened.");
    } finally {
      elements.webpFileInput.value = "";
      setBusy(false);
    }
  }

  async function convert() {
    if (!state.metadata || state.busy) return;
    showStatus();
    setBusy(true);
    const jobId = ++state.jobId;
    const { metadata, bytes } = state;
    const canvas = document.createElement("canvas");
    canvas.width = metadata.width;
    canvas.height = metadata.height;
    const context = canvas.getContext("2d", { alpha: true, willReadFrequently: true });

    try {
      elements.webpProgress.textContent = "Preparing GIF encoder…";
      await sendWorker("init", {
        jobId,
        width: metadata.width,
        height: metadata.height,
        colors: Number(elements.gifColors.value),
        repeat: metadata.animated ? gifRepeatCount(metadata.loopCount) : -1
      });

      paintBackground(context, metadata);
      const frames = metadata.frames;
      for (let index = 0; index < frames.length; index += 1) {
        const frame = frames[index];
        elements.webpProgress.textContent = `Encoding frame ${index + 1} of ${frames.length}…`;
        const blob = metadata.animated
          ? new Blob([webPFrameBytes(bytes, metadata, frame)], { type: "image/webp" })
          : state.file;
        const image = await decodedImage(blob);
        if (!frame.blend) paintBackground(context, { ...metadata, background: [0, 0, 0, 0] }, frame);
        context.drawImage(image, frame.x, frame.y, frame.width, frame.height);
        closeImage(image);
        const rgba = context.getImageData(0, 0, metadata.width, metadata.height).data;
        await sendWorker("frame", {
          jobId,
          rgba: rgba.buffer,
          delay: normalizeGifDelay(frame.duration)
        }, [rgba.buffer]);
        if (frame.dispose) paintBackground(context, metadata, frame);
        await new Promise(requestAnimationFrame);
      }

      elements.webpProgress.textContent = "Finishing GIF…";
      const encoded = await sendWorker("finish", { jobId });
      const output = new Blob([encoded.bytes], { type: "image/gif" });
      result.show({
        blob: output,
        fileName: `${safeBaseName(state.file?.name, "animation")}.gif`,
        message: metadata.animated
          ? "Your animated WebP was converted successfully."
          : "Your WebP image was converted successfully.",
        details: [
          `${metadata.width} × ${metadata.height} pixels`,
          metadata.animated ? `${metadata.frames.length} animation frames` : "Static GIF",
          `Up to ${elements.gifColors.value} colors per frame`
        ],
        downloadLabel: "Download GIF"
      });
    } catch (error) {
      try { await sendWorker("cancel", { jobId }); } catch { /* worker may already be gone */ }
      showStatus(error?.message || "The GIF could not be created.");
      window.toolboxAnalytics?.track("tool_error", { error_category: "processing" });
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      setBusy(false);
    }
  }

  const choose = () => elements.webpFileInput.click();
  elements.chooseWebp.addEventListener("click", event => { event.stopPropagation(); choose(); });
  elements.replaceWebp.addEventListener("click", choose);
  elements.webpFileInput.addEventListener("change", () => load(elements.webpFileInput.files?.[0]));
  elements.convertWebp.addEventListener("click", convert);
  elements.webpGifDropZone.addEventListener("click", event => { if (!event.target.closest("button")) choose(); });
  elements.webpGifDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose();
    }
  });
  elements.webpGifDropZone.addEventListener("dragover", event => {
    event.preventDefault();
    elements.webpGifDropZone.classList.add("is-dragging");
  });
  elements.webpGifDropZone.addEventListener("dragleave", () => elements.webpGifDropZone.classList.remove("is-dragging"));
  elements.webpGifDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.webpGifDropZone.classList.remove("is-dragging");
    load(event.dataTransfer.files?.[0]);
  });
  window.addEventListener("pagehide", cleanup);
}
